/**
 * Time Granularity Module
 *
 * Generic utilities for aggregating monthly data into coarser time buckets
 * (terminals, quarters, etc.). Used across Strategy and Dashboard views.
 *
 * The core pattern:
 *   1. Monthly data is produced by policy calculators (Buffer, Logistics, Coverage, etc.)
 *   2. This module buckets those monthly rows by boundary months (terminal months, quarter ends, etc.)
 *   3. A caller-supplied `aggregateBucket` callback defines how to merge the rows in each bucket
 *   4. A generic running state is threaded across buckets for chaining (e.g., running stock)
 *
 * ── Horizon model ──────────────────────────────────────────────────────────
 * All modes operate on a ROLLING HORIZON from the current month forward (default 24 months).
 * Buckets are calendar-aligned (Q1 = Jan-Mar, Year = Jan-Dec) but the first bucket in
 * the horizon will be partial (e.g., if today is May, Q2 only contains May + Jun).
 *
 * ── Historical data limitation ─────────────────────────────────────────────
 * The spec calls for a full CALENDAR YEAR view (Jan-Dec regardless of current month),
 * but this is NOT currently achievable because position snapshots overwrite previous
 * data on each import — past months' data does not exist in the system.
 * Once we retain historical snapshots (or build a time-series layer), the Quarter
 * and Year modes should be updated to support a true calendar view as a separate
 * "calendar" vs "rolling" option on the toggle.
 */

// ── Types ──────────────────────────────────────────────────────────────────────

/**
 * Month     — raw monthly rows, no aggregation (rolling horizon)
 * Quarter   — calendar quarters (Jan-Mar, Apr-Jun, …), rolling horizon, first bucket partial
 * Terminal  — user-defined boundary months per formula, rolling horizon
 * Yearly    — calendar year (Jan-Dec), rolling horizon, first bucket partial
 * Full      — entire horizon collapsed into a single bucket
 *
 * NOTE: Quarter and Year are rolling-horizon only for now. Full calendar views
 * require historical data retention which is not yet implemented. See module
 * header comment for details.
 */
export type TimeGranularity = 'Month' | 'Quarter' | 'Terminal' | 'Yearly' | 'Full';

export const MONTH_NAMES = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'] as const;

/** Human-readable labels for each granularity (used in detail panel headers/disclaimers) */
export const GRANULARITY_LABELS: Record<TimeGranularity, string> = {
  Month: 'Monthly',
  Quarter: 'Quarter',
  Yearly: 'Year',
  Terminal: 'Terminal',
  Full: 'Full Horizon',
};

export const QUARTER_END_MONTHS = [2, 5, 8, 11]; // Mar, Jun, Sep, Dec (0-indexed)
export const YEAR_END_MONTHS = [11]; // Dec (0-indexed)

/** A resolved boundary period in the upcoming horizon */
export interface BoundaryPeriod {
  monthIdx: number;       // 0-11 calendar month
  year: number;
  absoluteMonth: number;  // offset from current month (0 = this month)
  label: string;          // e.g. "Mar25"
}

// ── Helpers ────────────────────────────────────────────────────────────────────

/** Parse a month label like "Mar25" → { monthIndex: 2, yearStr: "25" } */
export function parseMonthLabel(label: string): { monthIndex: number; yearStr: string } | null {
  const monthStr = label.substring(0, 3);
  const monthIndex = MONTH_NAMES.indexOf(monthStr as typeof MONTH_NAMES[number]);
  if (monthIndex === -1) return null;
  return { monthIndex, yearStr: label.substring(3) };
}

/** Format a month index + year into a label: (2, 2025) → "Mar25" */
export function formatMonthLabel(monthIdx: number, year: number): string {
  return MONTH_NAMES[monthIdx] + String(year).substring(2);
}

/**
 * Find upcoming boundary periods in the next `horizonMonths` months.
 * Works for terminals, quarters, or any set of boundary months.
 */
export function getBoundaryPeriods(
  boundaryMonths: number[],
  horizonMonths: number = 24,
): BoundaryPeriod[] {
  const now = new Date();
  const currentMonth = now.getMonth();
  const currentYear = now.getFullYear();

  const periods: BoundaryPeriod[] = [];
  for (let i = 0; i < horizonMonths; i++) {
    const d = new Date(currentYear, currentMonth + i, 1);
    const monthIdx = d.getMonth();
    if (boundaryMonths.includes(monthIdx)) {
      periods.push({
        monthIdx,
        year: d.getFullYear(),
        absoluteMonth: i,
        label: formatMonthLabel(monthIdx, d.getFullYear()),
      });
    }
  }
  return periods;
}

/**
 * Build a lookup map from monthly data: key = "{monthIndex}-{yearStr}"
 * e.g. "2-25" for Mar 2025
 * @param getLabel - optional accessor for the month label (defaults to `item.month`)
 */
export function buildMonthlyDataMap<T>(
  data: T[],
  getLabel: (item: T) => string = (item: any) => item.month,
): Map<string, T> {
  const map = new Map<string, T>();
  for (const item of data) {
    const parsed = parseMonthLabel(getLabel(item));
    if (parsed) {
      map.set(`${parsed.monthIndex}-${parsed.yearStr}`, item);
    }
  }
  return map;
}

// ── Core Aggregation ───────────────────────────────────────────────────────────

export interface AggregateConfig<T, S> {
  /** Monthly rows to aggregate (exclude any On-Hand row — caller manages that) */
  monthlyData: T[];
  /** Boundary months (0-indexed), e.g. [2,5,8,11] for quarterly or formula-specific terminals */
  boundaryMonths: number[];
  /** Horizon in months (default 24) */
  horizonMonths?: number;
  /** Initial running state (e.g. initial stock from On-Hand) */
  initialState: S;
  /**
   * Called once per boundary period with the monthly items in that bucket.
   * Must return the aggregated row and the next running state.
   */
  aggregateBucket: (
    items: T[],
    label: string,
    absoluteMonth: number,
    state: S,
  ) => { row: T; nextState: S };
  /** Optional: accessor for the month label field (defaults to `item.month`) */
  getLabel?: (item: T) => string;
  /** Optional: key for storing debug log on window (e.g. "__bufferTerminalDebugLog") */
  debugKey?: string;
}

/**
 * Aggregate monthly data rows into boundary-period buckets.
 *
 * Each bucket spans from the month *after* the previous boundary up to and including
 * the current boundary month. A caller-supplied callback handles the actual
 * field-level aggregation (sums, averages, running chains, etc.).
 *
 * @returns Array of aggregated rows, one per boundary period.
 */
export function aggregateToBoundaryPeriods<T, S>(
  config: AggregateConfig<T, S>,
): T[] {
  const {
    monthlyData,
    boundaryMonths,
    horizonMonths = 24,
    initialState,
    aggregateBucket,
    getLabel,
    debugKey,
  } = config;

  const map = buildMonthlyDataMap(monthlyData, getLabel);
  const periods = getBoundaryPeriods(boundaryMonths, horizonMonths);

  const now = new Date();
  const currentMonth = now.getMonth();
  const currentYear = now.getFullYear();

  const debugLog: string[] = [];
  if (debugKey) {
    debugLog.push('='.repeat(60));
    debugLog.push(`TERMINAL AGGREGATION (${debugKey})`);
    debugLog.push('='.repeat(60));
    debugLog.push(`Boundaries: ${boundaryMonths.map(m => MONTH_NAMES[m]).join(', ')}`);
    debugLog.push(`Periods found: ${periods.length}`);
    debugLog.push('');
  }

  const result: T[] = [];
  let state = initialState;

  for (let ti = 0; ti < periods.length; ti++) {
    const period = periods[ti];
    const startAbs = ti === 0 ? 0 : periods[ti - 1].absoluteMonth + 1;
    const endAbs = period.absoluteMonth;

    // Gather monthly items in this bucket
    const items: T[] = [];
    for (let i = startAbs; i <= endAbs; i++) {
      const d = new Date(currentYear, currentMonth + i, 1);
      const key = `${d.getMonth()}-${String(d.getFullYear()).substring(2)}`;
      const item = map.get(key);
      if (item) items.push(item);
    }

    if (debugKey) {
      debugLog.push(`${period.label}: ${items.length} months (abs ${startAbs}-${endAbs})`);
    }

    const { row, nextState } = aggregateBucket(items, period.label, period.absoluteMonth, state);
    result.push(row);
    state = nextState;
  }

  if (debugKey) {
    debugLog.push('');
    debugLog.push(`✅ ${result.length} buckets`);
    debugLog.push('='.repeat(60));
    (window as any)[debugKey] = debugLog.join('\n');
  }

  return result;
}

/**
 * Aggregate ALL monthly data into a single bucket (Full Horizon mode).
 * Uses the same aggregateBucket callback as aggregateToBoundaryPeriods,
 * so callers can share the same aggregation logic for both modes.
 */
export function aggregateToSingleBucket<T, S>(
  config: Omit<AggregateConfig<T, S>, 'boundaryMonths'>,
): T[] {
  const { monthlyData, initialState, aggregateBucket } = config;
  if (monthlyData.length === 0) return [];
  const lastItem = monthlyData[monthlyData.length - 1] as any;
  const label = lastItem?.month ?? 'All';
  const { row } = aggregateBucket(monthlyData, `Full`, monthlyData.length - 1, initialState);
  return [row];
}

// ── Convenience: resolve boundary months from granularity ──────────────────────

/**
 * Given a TimeGranularity and optional terminal months, return the boundary months
 * to use for aggregation. Returns null if granularity is 'Month' (no aggregation needed).
 */
export function resolveBoundaryMonths(
  granularity: TimeGranularity,
  terminals?: number[] | null,
  horizonMonths: number = 24,
): number[] | null {
  switch (granularity) {
    case 'Month':
      return null; // No aggregation
    case 'Quarter':
      return QUARTER_END_MONTHS;
    case 'Yearly':
      return YEAR_END_MONTHS;
    case 'Terminal':
      return terminals && terminals.length > 0 ? terminals : null;
    case 'Full':
      return null; // Full aggregation is handled separately by callers (single-bucket)
    default:
      return null;
  }
}
